import React, { useEffect, useMemo, useRef, useState, memo } from "react";
import { useNavigate } from "react-router-dom";
import SolidarityIcon from "../Assets/Icons/SolidarityIcon";
import { toast, ToastContainer } from "react-toastify";
import { Reset } from "./API/UserAuthServices";
// import EkeyLogin from "./EkeyLogin";

// Icons
import {
  UserCircleIcon,
  LockClosedIcon,
  EyeIcon,
  EyeSlashIcon,
  ArrowRightOnRectangleIcon,
  KeyIcon,
} from "@heroicons/react/24/outline";

/* ---------------------------- Stable Shell ---------------------------- */
/* Moved OUTSIDE and memoized so it won't remount while typing */
const Shell = memo(function Shell({ children, title, subtitle, icon }) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 via-white to-sky-50">
      <div className="mx-auto flex min-h-screen max-w-7xl items-center px-4 sm:px-6 lg:px-8">
        <div className="grid w-full gap-8 lg:grid-cols-2">
          {/* Left brand/message (hidden on mobile) */}
          <div className="hidden lg:flex flex-col justify-center">
            <div className="flex items-center gap-3">
              <SolidarityIcon className="h-10 w-auto" size="h-10" />
              <div className="text-2xl font-semibold text-gray-900">
                Solidarity Hub
              </div>
            </div>
            <p className="mt-6 max-w-md text-sm text-gray-600">
              Secure access to internal tools and analytics. Please sign in
              using your corporate username (e.g.,{" "}
              <span className="font-mono">john.doe</span>) and your password.
            </p>

            <div className="mt-10 hidden xl:block">
              <div className="rounded-2xl border border-gray-200 bg-white/70 p-5 shadow-sm backdrop-blur">
                <div className="text-sm font-medium text-gray-900">
                  Need access?
                </div>
                <p className="mt-1 text-sm text-gray-600">
                  Create a ticket and our IT team will assist you.
                </p>
                <a
                  href="https://solidarity.sdpondemand.manageengine.com/app/itdesk/ui/requests/add?reqTemplate=73565000032112301"
                  className="mt-3 inline-flex items-center gap-2 rounded-lg bg-sky-500 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-sky-600"
                >
                  <ArrowRightOnRectangleIcon className="h-4 w-4" />
                  Create a ticket
                </a>
              </div>
            </div>
          </div>

          {/* Right card */}
          <div className="flex w-full justify-center">
            <div className="w-full max-w-md">
              <div className="mb-6 flex items-center gap-3">
                <div className="grid h-10 w-10 place-items-center rounded-xl bg-gradient-to-br from-sky-500 to-sky-600 text-white shadow-sm">
                  {icon}
                </div>
                <div>
                  <h1 className="text-xl font-semibold text-gray-900">
                    {title}
                  </h1>
                  <p className="text-sm text-gray-500">{subtitle}</p>
                </div>
              </div>

              <div className="rounded-2xl border border-gray-200 bg-white/80 p-6 shadow-sm backdrop-blur">
                {children}
              </div>

              <p className="mt-8 text-center text-sm text-gray-500">
                Don&apos;t have access?{" "}
                <a
                  href="https://solidarity.sdpondemand.manageengine.com/app/itdesk/ui/requests/add?reqTemplate=73565000032112301"
                  className="font-semibold text-sky-600 hover:text-sky-700"
                >
                  Create a ticket
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
      <ToastContainer />
    </div>
  );
});

/* ----------------------------- Login Page ----------------------------- */

const Login = () => {
  const navigate = useNavigate();

  // If launched from an installed PWA (standalone mode), bounce to /document-search.
  // This catches cases where a home-screen shortcut was cached with start_url=/.
  useEffect(() => {
    const isStandalone =
      window.matchMedia?.("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    if (isStandalone) {
      navigate("/document-search", { replace: true });
    }
  }, [navigate]);

  // UI state
  const [isLoading, setLoading] = useState(false);
  const [isReset, setIsReset] = useState(false);
  const [error, setError] = useState("");

  // Login state
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);

  // Reset state
  const [Token, setToken] = useState("");
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  // Refs to preserve focus/caret when toggling visibility
  const usernameRef = useRef(null);
  const passwordRef = useRef(null);
  const oldPassRef = useRef(null);
  const newPassRef = useRef(null);
  const confirmPassRef = useRef(null);

  // derived validations
  const usernameHasAt = useMemo(() => username.includes("@"), [username]);
  const newPassMismatch = useMemo(
    () => !!newPassword && !!confirmPassword && newPassword !== confirmPassword,
    [newPassword, confirmPassword]
  );

  const handleChange = (e) => setUsername(e.target.value);

  // IMPORTANT: sanitize on change/paste only (no onBlur) to avoid blur/re-mount issues
  const handlePassChange = (e) =>
    setPassword(e.target.value.replace(/\s+/g, ""));

  function toggleVisibility(setter, inputRef) {
    const el = inputRef?.current;
    const selStart = el?.selectionStart ?? null;
    const selEnd = el?.selectionEnd ?? null;
    setter((s) => !s);
    requestAnimationFrame(() => {
      if (el) {
        el.focus({ preventScroll: true });
        if (selStart !== null && selEnd !== null) {
          try {
            el.setSelectionRange(selStart, selEnd);
          } catch {}
        }
      }
    });
  }

  const LoginAPI = async () => {
    const url = (process.env.REACT_APP_PAYMENT_API || "") + "Account/LoginUser";
    const data = { UserName: username, Password: password };

    try {
      setLoading(true);
      setError("");
      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorDetails = await response.json().catch(() => ({}));
        toast.error(errorDetails.message || "Failed to login", {
          style: { background: "#ffefef", color: "#7f1d1d", fontWeight: 600 },
        });
        throw new Error("Failed to login");
      }

      const result = await response.json();

      if (result.message === "Ok") {
        const isoDate = (result.expiry || "").replace(" ", "T");
        sessionStorage.setItem("token", result.token || "");
        sessionStorage.setItem("expiry", isoDate || "");
        sessionStorage.setItem("username", result.userName || "");
        sessionStorage.setItem("department", result.department || "");
        sessionStorage.setItem("position", result.position || "");
        sessionStorage.setItem("requestLimit", result.requestLimit || "");
        sessionStorage.setItem("ownerOf", JSON.stringify(result.ownerOf || []));
        sessionStorage.setItem(
          "hasAccessTo",
          JSON.stringify(result.hasAccessTo || [])
        );
        sessionStorage.setItem(
          "requestTypeAccess",
          JSON.stringify(result.requestTypeAccess || [])
        );
        sessionStorage.setItem("profilePath", result.profilePath || "");
        setLoading(false);

        navigate("/hub?page=Home");
      } else if (result.message === "Reset Password") {
        setToken(result.token || "");
        setLoading(false);
        setIsReset(true);
      } else {
        setLoading(false);
        toast.error(result.message || "Login failed", {
          style: { background: "#ffefef", color: "#7f1d1d", fontWeight: 600 },
        });
      }
    } catch (err) {
      setLoading(false);
      console.error("Login error:", err);
    }
  };

  const handleLogin = async () => {
    if (!username || !password) {
      setError("Both username and password are required!");
      return;
    }
    await LoginAPI();
  };

  const handleReset = async () => {
    if (!oldPassword || !newPassword || !confirmPassword) {
      setError("All fields are required!");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    setError("");
    await Reset(
      Token,
      oldPassword,
      toast,
      newPassword,
      confirmPassword,
      setLoading
    );
  };

  const GotoForgetPass = () => {
    navigate("/forgot-password");
  };

  if (!isReset) {
    return (
      <Shell
        title="Sign in"
        subtitle="Use your Solidarity Hub username and password."
        icon={<UserCircleIcon className="h-5 w-5" />}
      >
        <form className="space-y-5" onSubmit={(e) => e.preventDefault()}>
          {/* Username */}
          <div>
            <label
              htmlFor="username"
              className="block text-sm font-medium text-gray-900"
            >
              Username
            </label>
            <div className="mt-1 relative">
              <input
                ref={usernameRef}
                id="username"
                name="username"
                type="text"
                autoComplete="username"
                placeholder="e.g. john.doe (not an email)"
                value={username}
                onChange={handleChange}
                pattern="^[a-zA-Z0-9._-]+$"
                title="Enter your username only — no @ or domain"
                aria-describedby="username-hint username-error"
                aria-invalid={usernameHasAt}
                className={`block w-full rounded-lg border-0 py-2 pr-3 pl-10 text-gray-900 shadow-sm ring-1 ring-inset placeholder:text-gray-400 sm:text-sm ${
                  usernameHasAt
                    ? "ring-red-500 focus:ring-red-600"
                    : "ring-gray-300 focus:ring-sky-600"
                }`}
              />
              <UserCircleIcon className="pointer-events-none absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
            </div>
            <p id="username-hint" className="mt-1 text-xs text-gray-500">
              Username only (e.g., <span className="font-mono">john.doe</span>).
              Don’t enter your email.
            </p>
            {usernameHasAt && (
              <p id="username-error" className="mt-1 text-xs text-red-600">
                Remove the email part (e.g.,{" "}
                <span className="font-mono">@solidarity.com.bh</span>). This
                field is for your username only.
              </p>
            )}
          </div>

          {/* Password */}
          <div>
            <div className="flex items-center justify-between">
              <label
                htmlFor="password"
                className="block text-sm font-medium text-gray-900"
              >
                Password
              </label>
              {/* <button
                type="button"
                onClick={GotoForgetPass}
                className="text-xs font-semibold text-sky-600 hover:text-sky-700"
              >
                Forgot?
              </button> */}
            </div>

            <div className="mt-1 relative">
              <input
                ref={passwordRef}
                id="password"
                name="password"
                type={showPass ? "text" : "password"}
                value={password}
                onChange={handlePassChange}
                onKeyDown={(e) => {
                  if (e.key === " " || e.code === "Space") e.preventDefault();
                }}
                onPaste={(e) => {
                  e.preventDefault();
                  const pasted = e.clipboardData
                    .getData("text")
                    .replace(/\s+/g, "");
                  setPassword(pasted);
                }}
                autoComplete="current-password"
                inputMode="text"
                spellCheck={false}
                pattern="^\\S+$"
                title="No spaces allowed in password"
                className="block w-full rounded-lg border-0 py-2 pr-10 pl-10 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-sky-600 sm:text-sm"
              />
              <LockClosedIcon className="pointer-events-none absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()} // prevent focus theft
                onClick={() => toggleVisibility(setShowPass, passwordRef)}
                className="absolute right-2 top-1.5 rounded-md p-1 text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-sky-600"
                aria-label={showPass ? "Hide password" : "Show password"}
              >
                {showPass ? (
                  <EyeSlashIcon className="h-5 w-5" />
                ) : (
                  <EyeIcon className="h-5 w-5" />
                )}
              </button>
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Passwords cannot contain spaces.
            </p>
          </div>

          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
              {error}
            </div>
          )}

          <button
            type="button"
            onClick={handleLogin}
            disabled={isLoading}
            className="relative flex w-full items-center justify-center overflow-hidden rounded-lg bg-sky-500 px-3 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-sky-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-600 disabled:cursor-not-allowed disabled:opacity-70"
          >
            {isLoading ? (
              <span className="inline-flex items-center gap-2">
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                Signing in…
              </span>
            ) : (
              <span className="inline-flex items-center gap-2">
                <ArrowRightOnRectangleIcon className="h-4 w-4" />
                Sign in
              </span>
            )}
          </button>
        </form>
        {/* <EkeyLogin /> */}
      </Shell>
    );
  }

  // RESET PASSWORD
  return (
    <Shell
      title="Reset password"
      subtitle="Set a new password for your account."
      icon={<KeyIcon className="h-5 w-5" />}
    >
      <form className="space-y-5" onSubmit={(e) => e.preventDefault()}>
        {/* Current password */}
        <div>
          <label
            htmlFor="oldPassword"
            className="block text-sm font-medium text-gray-900"
          >
            Current password
          </label>
          <div className="mt-1 relative">
            <input
              ref={oldPassRef}
              id="oldPassword"
              name="oldPassword"
              type={showOld ? "text" : "password"}
              value={oldPassword}
              onChange={(e) => {
                // remove only trailing whitespace
                const v = e.target.value.replace(/\s+$/, "");
                setOldPassword(v);
              }}
              onPaste={(e) => {
                e.preventDefault();
                const pasted = e.clipboardData
                  .getData("text")
                  .replace(/\s+$/, "");
                setOldPassword(pasted);
              }}
              className="block w-full rounded-lg border-0 py-2 pr-10 pl-10 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-sky-600 sm:text-sm"
            />

            <LockClosedIcon className="pointer-events-none absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => toggleVisibility(setShowOld, oldPassRef)}
              className="absolute right-2 top-1.5 rounded-md p-1 text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-sky-600"
              aria-label={showOld ? "Hide password" : "Show password"}
            >
              {showOld ? (
                <EyeSlashIcon className="h-5 w-5" />
              ) : (
                <EyeIcon className="h-5 w-5" />
              )}
            </button>
          </div>
        </div>

        {/* New password */}
        <div>
          <label
            htmlFor="newpassword"
            className="block text-sm font-medium text-gray-900"
          >
            New password
          </label>
          <div className="mt-1 relative">
            <input
              ref={newPassRef}
              id="newpassword"
              name="newpassword"
              type={showNew ? "text" : "password"}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="block w-full rounded-lg border-0 py-2 pr-10 pl-10 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-sky-600 sm:text-sm"
            />
            <LockClosedIcon className="pointer-events-none absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => toggleVisibility(setShowNew, newPassRef)}
              className="absolute right-2 top-1.5 rounded-md p-1 text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-sky-600"
              aria-label={showNew ? "Hide password" : "Show password"}
            >
              {showNew ? (
                <EyeSlashIcon className="h-5 w-5" />
              ) : (
                <EyeIcon className="h-5 w-5" />
              )}
            </button>
          </div>
        </div>

        {/* Confirm password */}
        <div>
          <label
            htmlFor="Confirmpassword"
            className="block text-sm font-medium text-gray-900"
          >
            Confirm new password
          </label>
          <div className="mt-1 relative">
            <input
              ref={confirmPassRef}
              id="Confirmpassword"
              name="Confirmpassword"
              type={showConfirm ? "text" : "password"}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className={`block w-full rounded-lg border-0 py-2 pr-10 pl-10 text-gray-900 shadow-sm ring-1 ring-inset placeholder:text-gray-400 sm:text-sm ${
                newPassMismatch
                  ? "ring-red-500 focus:ring-red-600"
                  : "ring-gray-300 focus:ring-sky-600"
              }`}
            />
            <LockClosedIcon className="pointer-events-none absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => toggleVisibility(setShowConfirm, confirmPassRef)}
              className="absolute right-2 top-1.5 rounded-md p-1 text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-sky-600"
              aria-label={showConfirm ? "Hide password" : "Show password"}
            >
              {showConfirm ? (
                <EyeSlashIcon className="h-5 w-5" />
              ) : (
                <EyeIcon className="h-5 w-5" />
              )}
            </button>
          </div>
          {newPassMismatch && (
            <p className="mt-1 text-xs text-red-600">
              New password and confirmation do not match.
            </p>
          )}
        </div>

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
            {error}
          </div>
        )}

        <button
          type="button"
          onClick={handleReset}
          disabled={isLoading}
          className="relative flex w-full items-center justify-center overflow-hidden rounded-lg bg-sky-500 px-3 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-sky-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-600 disabled:cursor-not-allowed disabled:opacity-70"
        >
          {isLoading ? (
            <span className="inline-flex items-center gap-2">
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
              Updating…
            </span>
          ) : (
            <span className="inline-flex items-center gap-2">
              <KeyIcon className="h-4 w-4" />
              Reset password
            </span>
          )}
        </button>
      </form>
    </Shell>
  );
};

export default Login;
